import { Container, Divider, Typography } from '@cleartrip/ct-design-components'
import React from 'react'

import { RootState } from '@/redux/store/configureStore'
import { useAppSelector } from '@/redux/store/hooks'

import InfoIcon from '@/assets/svg/infoIcon'
import { Stencil } from '@/components/common/Stencil'
import {
    AMEND,
    BENEFIT_NOT_INCLUDED,
    penalitiesTitleConfig,
    VISA_COVER_AVAILABLE_LABEL,
    VISA_COVER_FREE_LABEL,
    VISA_INFO_ICON_SIZE,
} from '@/constants/fareFamily'
import { benefitListData, cancellationChargesText } from '@/constants/Srp'
import { ServicesData } from '@/types/srp'
import { formatCurrency, replacePlaceholders } from '@/utils'
import { transformTimeStamp } from '@/utils/common'
import { isEmpty } from '@/utils/general/browserHelper'
import { getCardServicesData, getCommonCardServicesData, getSearchIntentDetails } from '@/utils/srp/helper'

import EliteAmendBenefitText from './EliteAmendBenefitText'
import CardBenefitsInfo from './FareBenefitsInfo'


type FareBenefitsProps = {
    servicesData: ServicesData
    travelOptionId: string
    fareId: string
    isServicesLoading: boolean
    retryCountForServices: number
    onRetryForBenefits: (id:any) => void
    showLeftTooltip?: boolean
    isSpecialRT?: boolean
    isIntl?: boolean
    isVisaAvailable?: boolean
    isVisaFree?: boolean
    onOpenVdcEducation?: () => void
}
type BaggageDataType = {
    benefitType: string
    text: string
    placeholderMap: object
}
const FareBenefits: React.FC<FareBenefitsProps> = ({
    isIntl = false,
    isVisaAvailable = false,
    isVisaFree = false,
    onOpenVdcEducation,
    servicesData,
    travelOptionId,
    fareId,
    isServicesLoading,
    retryCountForServices,
    onRetryForBenefits,
    showLeftTooltip = false,
    isSpecialRT = false,
}) => {
    const benefitsTooltipPosition = showLeftTooltip ? 'left' : 'right'
    const { commonServicesData, searchData } = useAppSelector((state: RootState) => state.srpSeachData)
    const { searchIntent, metaData } = searchData || {}
    const showVisaRow = isIntl && isVisaAvailable

    // Use the  farebenefits from a common service data state
    const { benefits, baggageAllowances } = commonServicesData || {}
    const showRetry = retryCountForServices < 2

    const getTooltipContent = (chargeInfos: any, type: string) => {
        if (isEmpty(chargeInfos)) {
            return ''
        }
        const timelines = chargeInfos.map((rule: any, index: number) => {
            const { permitted, totalCharges, total: totalRefund, fareRuleTimeline } = rule
            const displayCharges = type === AMEND ? totalCharges : totalRefund
            const { start, end } = fareRuleTimeline || {}

            return (
                <Container className="flex flex-middle flex-between mb-1">
                    <Typography className="ws-nowrap" color="neutral" variant="P3">
                        {index ? transformTimeStamp(start) : 'Now'} - {transformTimeStamp(end)}
                    </Typography>
                    <Typography className="ws-nowrap ml-6" color="neutral" variant="P3">
                        {!permitted && type === AMEND ? 'Non-changeable' : formatCurrency(displayCharges)}
                    </Typography>
                </Container>
            )
        })
        return (
            <Container className="flex flex-column mb-1">
                <Typography className="mb-2" color="neutral" variant="L3">
                    {penalitiesTitleConfig[type]}
                </Typography>
                {timelines}
                {type === 'CANCEL' && (
                    <>
                        <Container className="my-2" style={{borderBottom: '1.5px dashed #808080'}}/>
                        {cancellationChargesText}
                    </>
                )}
            </Container>
        )
    }
    const getSpecialRtTooltipContent = (specialRtChargeInfos: any, type: string) => {
        const { onwardsChargeInfos = [], returnChargeInfos = [] } = specialRtChargeInfos || {}
        
        if (isEmpty(onwardsChargeInfos) && isEmpty(returnChargeInfos)) {
            return ''
        }

        // Get dynamic route names from search intent
        const { J1from = '', J1to = '', J2from = '', J2to = '' } = getSearchIntentDetails(searchIntent) || {}
        
        // Get city names from metadata
        const onwardRoute = `${metaData?.airportDetail?.[J1from]?.city || J1from} → ${metaData?.airportDetail?.[J1to]?.city || J1to}`
        const returnRoute = `${metaData?.airportDetail?.[J2from]?.city || J2from} → ${metaData?.airportDetail?.[J2to]?.city || J2to}`

        const renderJourneySection = (chargeInfos: any[], sectionTitle: string) => {
            if (isEmpty(chargeInfos)) return null
            
            
            const timelines = chargeInfos.map((rule: any, index: number) => {
                const { permitted, totalCharges, total: totalRefund, fareRuleTimeline } = rule
                const displayCharges = type === AMEND ? totalCharges : totalRefund
                const { start, end } = fareRuleTimeline || {}

                return (
                    <Container key={index} className="flex flex-middle flex-between mt-1" >
                        <Typography className="ws-nowrap" color="neutral" variant="P3">
                            {index ? transformTimeStamp(start) : 'Now'} - {transformTimeStamp(end)}
                        </Typography>
                        <Typography className="ws-nowrap ml-6" color="neutral" variant="P3">
                            {!permitted && type === AMEND ? 'Non-changeable' : formatCurrency(displayCharges)}
                        </Typography>
                    </Container>
                )
            })

            return (
                <Container className="flex flex-column mb-3">
                    <Typography className="ws-nowrap" color="neutral" css={{ marginBottom: '6px' }} variant="L3">
                        {sectionTitle}
                    </Typography>
                    {timelines}
                </Container>
            )
        }

        const renderNoteSection = () => {
            return (
                <Container className="flex flex-column mb-2">
                    <Typography color="neutral" variant="P3">
                        <span style={{ fontWeight: '600', fontSize: '12px', lineHeight: '16px' }}>NOTE:</span> Partial cancellation is not allowed for this flight combination. Cancellation applies to the full journey.
                    </Typography>
                </Container>
            )
        }

        return (
            <Container className="flex flex-column mb-1 px-1">
                <Typography className='mb-2' color="neutral" variant="L2">
                    {penalitiesTitleConfig[type]}
                </Typography>
                
                {/* Onward Journey Section */}
                {renderJourneySection(onwardsChargeInfos, onwardRoute)}
                
                {/* Dotted Divider - only when both sections have data */}
                {!isEmpty(onwardsChargeInfos) && !isEmpty(returnChargeInfos) && (
                    <Divider borderColor="#E6E6E6" borderStyle="dashed" className='mb-3' />
                )}
                
                {/* Return Journey Section OR Note Section */}
                {!isEmpty(returnChargeInfos) ? (
                    renderJourneySection(returnChargeInfos, returnRoute)
                ) : !isEmpty(onwardsChargeInfos) ? (
                    <>
                        <Divider borderColor="#E6E6E6" borderStyle="dashed" className='mb-2' />
                        {renderNoteSection()}
                    </>
                ) : null}
            </Container>
        )
    }

    const getBenefitDisplayValue = (benefitType: string) => {
        let benefitValueObj = {
            infoIconContent: '',
            displayMessage: '',
            subText: '',
            isInfoAvailable: false,
            isLoading: true,
            primaryColor: 'primary',
            isEliteBenefit: false,
            eliteText: '',
            elitePlaceholderMap: {} as Record<string, any>
        }
        let displayMessage = ''
        let subText = ''
        let infoIconContent: any = ''
        let primaryColor = 'primary'
        let isEliteBenefit = false
        let eliteText = ''
        let elitePlaceholderMap: Record<string, any> = {}

        const cardServicesData: {
            baggageAllowanceId?: any
            benefitIds?: any[]
            penalty?: any[]
            specialRtChargeInfos?: any
        } = getCommonCardServicesData({ servicesData: commonServicesData, travelOptionId, fareId, isSpecialRT })

        const { baggageAllowanceId = '', benefitIds = [], penalty = [], specialRtChargeInfos = {} } = cardServicesData || {}

        if (benefitType === 'SEAT' || benefitType === 'MEAL') {
            benefitIds?.forEach(benefitId => {
                const { benefitSummary = [] } = benefits?.[benefitId] || {}
                if (benefitSummary?.[0]?.benefitType === benefitType) {
                    displayMessage = benefitSummary[0].text
                    if(displayMessage.length > 29) {
                        infoIconContent = displayMessage
                        displayMessage = displayMessage.slice(0, 29) + '...'
                    }
                }
            })
        }

        if (benefitType === 'HAND_BAGGAGE' || benefitType === 'CHECK_IN_BAGGAGE') {
            const baggageData: BaggageDataType[] = baggageAllowances?.[baggageAllowanceId]?.benefitSummary || []
            baggageData?.forEach(baggage => {
                const { benefitType: baggageBenefitType, text, placeholderMap } = baggage || {}
                if (baggageBenefitType === benefitType) {
                    if (benefitType === 'CHECK_IN_BAGGAGE' && placeholderMap?.piece === 0 && placeholderMap?.weight === 0) {
                        displayMessage = BENEFIT_NOT_INCLUDED
                        subText = ''
                        primaryColor = 'warning'
                    } else {
                        const [preimayText, secondaryText] = replacePlaceholders(text, placeholderMap).split('|')
                        displayMessage = preimayText
                        subText = secondaryText
                    }
                }
            })
        }

        if (benefitType === 'CANCEL' || benefitType === 'AMEND') {
            penalty?.forEach(penaltyObj => {
                const { benefitSummary, benefitList = [] } = penaltyObj || {}
                const selectedBenefitType = benefitList?.find?.((benefit: { benefitType: string }) => {
                    return benefit.benefitType === benefitType
                })
                if (!isEmpty(selectedBenefitType)) {
                    const { details } = selectedBenefitType || {}
                    const { fareRules } = details || {}
                    const { chargeInfos = [] } = fareRules || {}
                    
                    // Use different tooltip content based on Special RT case
                    if (isSpecialRT && !isEmpty(specialRtChargeInfos) && benefitType === 'AMEND') {
                        infoIconContent = getSpecialRtTooltipContent(specialRtChargeInfos, benefitType) || []
                    } else {
                        infoIconContent = getTooltipContent(chargeInfos, benefitType) || []
                    }
                }
                const { text, placeholderMap, benefitType: fareBenefit } = benefitSummary?.[0] || {}
                if (benefitType === fareBenefit) {
                    displayMessage = replacePlaceholders(text, placeholderMap)
                    // Elite date-change treatment only when the API copy says so
                    if (benefitType === AMEND && typeof text === 'string' && /\bElite\b/i.test(text)) {
                        isEliteBenefit = true
                        eliteText = text
                        elitePlaceholderMap = placeholderMap
                    }
                }
            })
        }

        if (isEmpty(displayMessage)) {
            if (isServicesLoading) {
                benefitValueObj = {
                    ...benefitValueObj,
                    isLoading: true
                }
            } else {
                benefitValueObj = {
                    ...benefitValueObj,
                    isLoading: false,
                    displayMessage: '',
                    infoIconContent,
                    isInfoAvailable: false
                }
            }
        } else {
            benefitValueObj = {
                ...benefitValueObj,
                infoIconContent,
                displayMessage,
                primaryColor,
                subText,
                isInfoAvailable: true,
                isLoading: false,
                isEliteBenefit,
                eliteText,
                elitePlaceholderMap
            }
        }
        return benefitValueObj
    }

    const getBenefitItem = (item: string) => {
        const {
            infoIconContent = '',
            displayMessage = '',
            subText = '',
            isInfoAvailable = false,
            isLoading,
            primaryColor,
            isEliteBenefit = false,
            eliteText = '',
            elitePlaceholderMap = {}
        } = getBenefitDisplayValue(item) || {}

        return (
            <>
                <Container className={`flex flex-middle py-3 ff-row-${item}`}>
                    {isLoading ? (
                        <Stencil shimmer borderRadius="2px" className="br-2" height="16px" width="100px" />
                    ) : !isInfoAvailable ? (
                        <>
                            <Typography className="ws-nowrap" color="warning" variant="P3">
                                Info not available
                            </Typography>
                            {showRetry ? (
                                <Container className="ml-1" onClick={()=>onRetryForBenefits(fareId)}>
                                    <Typography className="ws-nowrap c-pointer" color="link" variant="P3">
                                        Retry ↻
                                    </Typography>
                                </Container>
                            ) : (
                                ''
                            )}
                        </>
                    ) : isEliteBenefit ? (
                        // Text + pill + info icon flow as one sentence that wraps together
                        <Container css={{ maxWidth: '182px', lineHeight: '20px' }}>
                            <EliteAmendBenefitText placeholderMap={elitePlaceholderMap} text={eliteText} />
                            {!isEmpty(subText) && (
                                <Typography className="ml-1 d-inline" color="subheading" componentNode="span" variant="P3">
                                    {subText}
                                </Typography>
                            )}
                            {!isEmpty(infoIconContent) && (
                                <span className="flex-inline" style={{ verticalAlign: 'middle' }}>
                                    <CardBenefitsInfo
                                        message={infoIconContent}
                                        position={benefitsTooltipPosition}
                                        width="312px"
                                        changeDateFee={
                                            typeof elitePlaceholderMap?.amount === 'number'
                                                ? elitePlaceholderMap.amount
                                                : null
                                        }
                                    />
                                </span>
                            )}
                        </Container>
                    ) : (
                        <>
                            <Typography
                                className="ws-nowrap t-truncate"
                                color={primaryColor}
                                css={{ maxWidth: '182px' }}
                                variant="P3"
                            >
                                {displayMessage}
                            </Typography>
                            {!isEmpty(subText) && (
                                <Typography className="ml-1 ws-nowrap" color="subheading" variant="P3">
                                    {subText}
                                </Typography>
                            )}
                            {!isEmpty(infoIconContent) && (
                                <CardBenefitsInfo
                                    message={infoIconContent}
                                    position={benefitsTooltipPosition}
                                    width="312px"
                                />
                            )}
                        </>
                    )}
                </Container>
            </>
        )
    }
    const standardBenefits = benefitListData.filter(
        (benefit) => benefit.benefitKey !== 'VISA_REJECTION_COVER'
    )

    return (
        <Container className="flex flex-column flex-end pb-2">
            {standardBenefits.map((benefit, index) => {
                const { benefitKey } = benefit
                const showDivider = index < standardBenefits.length - 1 || showVisaRow
                return (
                    <>
                        {getBenefitItem(benefitKey)}
                        {showDivider && <Divider borderColor="#E6E6E6" borderStyle="dashed" />}
                    </>
                )
            })}
            {showVisaRow && (
                <Container className="flex flex-middle my-3">
                    {isServicesLoading ? (
                        <Stencil shimmer borderRadius="2px" className="br-2" height="16px" width="100px" />
                    ) : (
                        <>
                            <Typography color={isVisaFree ? 'success' : 'primary'} variant="P3">
                                {isVisaFree ? VISA_COVER_FREE_LABEL : VISA_COVER_AVAILABLE_LABEL}
                            </Typography>
                            <Container
                                className="ml-1 c-pointer"
                                style={{ cursor: 'pointer', marginBottom: '-3px' }}
                                onClick={() => onOpenVdcEducation?.()}
                            >
                                <InfoIcon height={VISA_INFO_ICON_SIZE} width={VISA_INFO_ICON_SIZE} />
                            </Container>
                        </>
                    )}
                </Container>
            )}
        </Container>
    )
}

export default FareBenefits
